import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supabase) {
      setError('Supabase client not initialized');
      setLoading(false);
      return;
    }

    // Check active sessions and sets the user
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (error) {
        console.error('Session error:', error);
        setError(error?.message);
      }
      setUser(session?.user ?? null);
      setLoading(false);
    })?.catch((err) => {
      console.error('Failed to get session:', err);
      setError(err?.message);
      setLoading(false);
    });

    // Listen for changes on auth state (signed in, signed out, etc.)
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    error,
    signUp: async (email, password, fullName) => {
      if (!supabase) throw new Error('Supabase client not initialized');
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
        }
      });
      if (error) throw error;
      return data;
    },
    signIn: async (email, password) => {
      if (!supabase) throw new Error('Supabase client not initialized');
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      return data;
    },
    signOut: async () => {
      if (!supabase) throw new Error('Supabase client not initialized');
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
    },
    resetPassword: async (email) => {
      if (!supabase) throw new Error('Supabase client not initialized');
      const { error } = await supabase?.auth?.resetPasswordForEmail(email);
      if (error) throw error;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};